import { useContext, useEffect, useMemo, useState } from "react";
import { Form, Select, message } from "antd";
import {
  createProjectIntegration,
  getCompanyAccounts
} from "services/integration-service";
import { IntegrationType } from "pages/subscription-settings/integrations-tab-new/utils";
import { FormInstance, useWatch } from "antd/lib/form/Form";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { ProjectIntegrationsTabSharedContext } from "pages/project-setting-menu/project-integrations/project-integrations-tab-context";

export default function WelcomeProcoreInitialIntegration(props: {
  integrationForm: FormInstance<any>;
  onProjectIntegrated: (response: any) => {};
}) {
  const { onProjectIntegrated, integrationForm } = props;
  const { sharedState, dispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const { tokenRetrievalState }: TProjectContext = useContext(ProjectContext);

  const [projects, setProjects] = useState<any>(null);

  const companies = useMemo(() => {
    const comp: any = [];
    const procoreSubConfigs = sharedState.subscriptionIntegrationConfigs.filter(
      (subConfig: any) => {
        return (
          subConfig.integration.system ===
          IntegrationType[IntegrationType.PROCORE]
        );
      }
    );
    procoreSubConfigs.forEach((procoreConfig: any) => {
      comp.push({
        name: procoreConfig.properties.PROCORE_COMPANY_NAME,
        id: procoreConfig.properties.PROCORE_COMPANY_ID,
        subscriptionId: procoreConfig.id
      });
    });
    return comp;
  }, [sharedState.subscriptionIntegrationConfigs]);

  const selectedCompanyId = useWatch("account_id", integrationForm);

  useEffect(() => {
    if (!selectedCompanyId || !tokenRetrievalState.token) return;
    const getAccounts = async () => {
      const foundCompany = companies.find((company: any) => {
        return company.id === selectedCompanyId;
      });

      // API has been changed in AutoDesk but it was decided to keep the same in Procore (CJ, HB)
      const accountsResp = await getCompanyAccounts(
        sharedState.system,
        {
          subscriptionConfigId: foundCompany.subscriptionId
        },
        tokenRetrievalState?.token
      );

      if (accountsResp.success) {
        setProjects(accountsResp.data);
      }
    };
    getAccounts();
  }, [
    companies,
    selectedCompanyId,
    sharedState.system,
    tokenRetrievalState.token
  ]);

  const onFinish = (values: any) => {
    const foundCompany = companies.find((company: any) => {
      return company.id === values.account_id;
    });

    const foundProject = projects.find((project: any) => {
      return project.id === values.project_id;
    });
    const payload: any = [
      {
        properties: {
          integration_name: sharedState?.additionalData.newIntegrationName,
          procore_project_id: values.project_id.toString(),
          procore_project_name: foundProject.name,
          project_alert_email: "integrations-nonprod@constructivIQ.com"
        },
        subscription_config_id: foundCompany.subscriptionId
      }
    ];

    const saveData = async () => {
      const createResponse = await createProjectIntegration(
        sharedState.system,
        payload,
        tokenRetrievalState.token
      );

      if (createResponse.success) {
        // message.success("Integration created successfully.");
        onProjectIntegrated(createResponse);
        dispatch({
          type: "SET_ACTIVE_LIST_VIEW"
        });
      } else {
        message.error("Failed to create integration.");
      }
    };
    saveData();
  };

  const companyListOptions = useMemo(() => {
    return companies
      ?.map((company: any) => {
        return {
          label: company.name,
          value: company.id
        };
      })
      .sort((c1: any, c2: any) =>
        c1.label.toLowerCase() > c2.label.toLowerCase() ? 1 : -1
      );
  }, [companies]);

  const projectListOptions = useMemo(() => {
    return projects
      ?.map((project: any) => {
        return {
          label: project.name,
          value: project.id
        };
      })
      .sort((p1: any, p2: any) =>
        p1.label.toLowerCase() > p2.label.toLowerCase() ? 1 : -1
      );
  }, [projects]);

  return (
    <Form
      form={integrationForm}
      onFinish={onFinish}
      layout="vertical"
      className="h-full flex flex-col py-16 w-full md:max-w-screen-sm mx-auto justify-center items-center "
    >
      <div className="mb-7 w-[500px] space-y-7">
        <Form.Item
          name="account_id"
          className="hide-required-mark"
          label={
            <div className="flex items-center">
              <span>ACCOUNT</span>
            </div>
          }
          rules={[{ required: true, message: "Please select an Account" }]}
          requiredMark={false}
        >
          <Select
            showSearch
            optionFilterProp="label"
            // mode="multiple"
            options={companyListOptions}
            // tagRender={tagRender}
            placeholder="Select an Account"
            className="normal-case"
            loading={!companyListOptions}
            allowClear={false}
            showArrow
          />
        </Form.Item>
        <Form.Item
          name="project_id"
          className="hide-required-mark"
          label={
            <div className="flex items-center">
              <span>PROJECT</span>
            </div>
          }
          rules={[{ required: true, message: "Please select a Project" }]}
          requiredMark={false}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={projectListOptions}
            placeholder="Select a Project"
            className="normal-case"
            loading={selectedCompanyId && !projectListOptions}
            allowClear={false}
            showArrow
            disabled={!selectedCompanyId || !projectListOptions}
          />
        </Form.Item>
      </div>
    </Form>
  );
}
